import { useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';

import { MeasureResponseCompletionCampaign } from '@grow-therapy-team/seedling-components';

import { PageComponentProps } from '../../components';
import { MeasuresActionsWrapper } from '../../components/measures/MeasuresActions';
import { MeasureSchemaAndAnswers } from '../../components/measures/types';
import { useGetClientUserShortId } from '../measures/useGetClientUserShortId';
import {
  didManuallyResolveSoftBlockAtom,
  hasCompletedClientMeasuresAtom,
  isSoftBlockResolvedAtom,
  visitorMeasuresAtom,
} from '../state';
import { useRecordClientMeasure } from '../useRecordClientMeasure';

type Props = PageComponentProps<MeasureSchemaAndAnswers> & {
  onAllMeasuresCompleted: () => void;
};

export function PreSessionMeasuresActions({ onAllMeasuresCompleted, ...pageProps }: Props) {
  const t = useTranslate();
  const clientUserShortId = useGetClientUserShortId();
  const { providerShortId } = useParams();
  const setHasCompletedClientMeasures = useSetAtom(hasCompletedClientMeasuresAtom);
  const setIsSoftBlockResolved = useSetAtom(isSoftBlockResolvedAtom);
  const setDidManuallyResolveSoftBlock = useSetAtom(didManuallyResolveSoftBlockAtom);
  const setVisitorMeasures = useSetAtom(visitorMeasuresAtom);
  const [recordMeasure] = useRecordClientMeasure(providerShortId);
  const submittedMeasureSchemaShortId = pageProps.page?.schema?.measureSchemaShortId;

  const handleSubmitMeasureSuccess = () => {
    if (providerShortId) {
      recordMeasure();
    }
    // Drop the just-submitted measure from the shared outstanding set so the
    // waiting-room drawer doesn't re-prompt for measures completed here.
    setVisitorMeasures(
      (prev) =>
        prev?.filter((measure) => measure.schema.measureSchemaShortId !== submittedMeasureSchemaShortId) ?? prev,
    );
    if (pageProps.isLastPage) {
      setHasCompletedClientMeasures(true);
      setIsSoftBlockResolved(true);
      setDidManuallyResolveSoftBlock(true);
      onAllMeasuresCompleted();
    }
  };

  return (
    <MeasuresActionsWrapper
      {...pageProps}
      clientUserShortId={clientUserShortId}
      providerShortId={providerShortId}
      completionCampaign={MeasureResponseCompletionCampaign.WAITING_ROOM}
      onSubmitMeasureSuccess={handleSubmitMeasureSuccess}
      errorScrollContainerRef={pageProps.scrollContainerRef}
      hidePreviousButton
      size='sm'
      submitButtonUse='primary'
      submitButtonLabel={
        pageProps.isLastPage
          ? t('telehealth-core:preSessionMeasures.submitAndJoin')
          : t('telehealth-core:core.continue')
      }
    />
  );
}
